import { TransactionBalancesWorkerMessage } from "@bitwarden/web-vault/app/services/web-worker/transaction-balances/transaction-balances.worker.message";
import { WebWorkerImplementation } from "@bitwarden/web-vault/web-worker/web.worker.implementation";

export class TransactionBalancesWorker extends WebWorkerImplementation<TransactionBalancesWorkerMessage> {
  _callback: (data: any) => void;
  _resolve: (value?: any) => void;
  _reject: (reason?: any) => void;
  _workerType = "TransactionBalancesWorker";

  protected constructor() {
    const worker = new Worker(new URL("./transaction-balances.job.ts", import.meta.url), {
      type: "module",
    });
    super(worker);

    this.addEventListener("message", this.onMessageFromWorker.bind(this));
  }

  get callback(): (data: any) => void {
    return this._callback;
  }
  set callback(callback: (data: any) => void) {
    this._callback = callback;
  }

  static getInstance(): TransactionBalancesWorker {
    return new TransactionBalancesWorker();
  }

  // A failed post (e.g. DataCloneError) throws to WebWorkerQueue.runJob, which settles the job.
  async getBalanceForTransactions(message: TransactionBalancesWorkerMessage) {
    this.postMessage(message, [message.transactionAmounts]);
  }

  /**
   * function to handle the message back from the worker service
   * @param message
   */
  onMessageFromWorker(message: MessageEvent): void {
    if (message.data.complete) {
      // Clear before firing: this worker instance is reused for the next job
      // at this slot, and a stray/delayed "error" or "messageerror" event for
      // *this already-settled* job must find resolve/reject gone instead of
      // re-firing (or misfiring against whatever job reused the slot next).
      const resolve = this.resolve;
      const reject = this.reject;
      this.resolve = null;
      this.reject = null;
      if (message.data.error && reject) {
        reject(message.data.error);
      } else if (resolve) {
        resolve(message.data);
      } else if (reject) {
        reject(message.data);
      }
    }
  }

  protected onWorkerError(event: ErrorEvent | MessageEvent): void {
    // The underlying Worker crashed/failed to load — no "message" is coming.
    // Without this, resolve/reject never fire and the caller hangs forever.
    // See onMessageFromWorker: clear first so this same event can't also
    // double-fire if the browser dispatches both "error" and "messageerror"
    // for one failure, and so it can't fire again later.
    const reject = this.reject;
    this.resolve = null;
    this.reject = null;
    if (reject) {
      reject(event instanceof ErrorEvent ? event.message : event);
    }
  }

  get resolve(): (value?: any) => void {
    return this._resolve;
  }
  set resolve(resolve: (value?: any) => void) {
    this._resolve = resolve;
  }

  get reject(): (value?: any) => void {
    return this._reject;
  }
  set reject(reject: (value?: any) => void) {
    this._reject = reject;
  }
}
